// ── Sterling LIMS — Server Store ──────────────────────────────────────────────
// Single source of truth: server's data.json
// All reads from in-memory cache, populated on page load
// All writes go to server immediately

export const SERVER_URL = (() => {
  const normalize = (url) => String(url || '').trim().replace(/\/+$/, '');
  if (typeof window === 'undefined') return 'http://localhost:3001';

  // Optional explicit override. For Create React App, define:
  // REACT_APP_SERVER_URL=https://your-api-host.example.com
  // A runtime override can also be set before the app bundle loads:
  // window.__LIMS_SERVER_URL__ = 'https://your-api-host.example.com'
  const envUrl = typeof process !== 'undefined'
    ? process.env.REACT_APP_SERVER_URL
    : '';
  const configuredUrl = normalize(window.__LIMS_SERVER_URL__ || envUrl);
  if (configuredUrl) return configuredUrl;

  const { protocol, hostname, port, origin } = window.location;

  // A build opened directly from disk has no usable same-origin API.
  if (protocol === 'file:') return 'http://localhost:3001';

  // If accessed locally, React development normally uses :3000 and the API
  // uses :3001. If index.js already serves the page from :3001, preserve it.
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (port === '3001') return origin;
    return `${protocol}//${hostname}:3001`;
  }

  // Network development: React on :3000, API on :3001.
  if (port === '3000') return `${protocol}//${hostname}:3001`;

  // Production is served/proxied from the same origin. `origin` deliberately
  // preserves non-standard ports; the previous protocol+hostname expression
  // silently dropped them and produced "Failed to fetch".
  return origin;
})();

export const MAPI_URL = 'http://localhost:3001';

// Confirms in DevTools which compiled file is active and which backend it is
// calling. If this line does not appear after deployment, the browser is
// still using an older build/cache.
if (typeof window !== 'undefined') {
  console.info(`[Sterling LIMS] Backend URL: ${SERVER_URL}`);
}

// In-memory cache — wiped on every page load, populated from server
let _db = {};
let _ready = false; // blocks writes until initial sync completes
let _writeTime = {}; // tracks when each key was last written locally
let _resetVersion = 0; // tracks server reset version
let _knownVersion = 0; // tracks the server's cheap data-version counter, to avoid re-fetching/re-comparing the whole database every poll when nothing changed
let _pendingWrites = []; // writes attempted before sync finished — applied once _ready becomes true, instead of being silently lost
let _writeQueue = Promise.resolve();
let _syncPaused = false;
let _pendingWriteCount = 0;
let _writeSequence = {};
const _uiEditLocks = new Set();
const cloneValue = value => value === undefined ? undefined : JSON.parse(JSON.stringify(value));

const apiFetch = (url, options = {}) => fetch(url, {
  credentials: 'include',
  ...options,
});

function enqueueWrite(work) {
  _pendingWriteCount += 1;
  const write = _writeQueue.catch(() => {}).then(work);
  const tracked = write.finally(() => {
    _pendingWriteCount = Math.max(0, _pendingWriteCount - 1);
  });
  _writeQueue = tracked.catch(e =>
    console.error(`[LIMS] Confirmed server write failed (${SERVER_URL}):`, e.message)
  );
  return tracked;
}

// Forms use these locks while a person has unsaved edits. Record collections
// are not refreshed while locked: keeping their previous revision guarantees
// that a concurrent server edit produces HTTP 409 instead of being silently
// overwritten by the still-open form.
export function beginUiEdit(owner) {
  if (owner) _uiEditLocks.add(String(owner));
}

export function endUiEdit(owner) {
  if (owner) _uiEditLocks.delete(String(owner));
  // Force one complete comparison after the final form lock is released.
  if (_uiEditLocks.size === 0) _knownVersion = 0;
}

function replaceCached(collectionKey, identity, record) {
  const records = Array.isArray(_db[collectionKey]) ? [..._db[collectionKey]] : [];
  const index = records.findIndex(identity);
  if (index >= 0) records[index] = record;
  else records.unshift(record);
  _db[collectionKey] = records;
}

function removeCached(collectionKey, identity) {
  const records = Array.isArray(_db[collectionKey]) ? _db[collectionKey] : [];
  _db[collectionKey] = records.filter(item => !identity(item));
}

function applySampleIdentityToCache(oldId, record) {
  const previousId = String(oldId);
  const nextId = String(record.id);
  removeCached('sl_samples', item => String(item.id) === previousId);
  replaceCached('sl_samples', item => String(item.id) === nextId, record);

  if (previousId === nextId) return;
  for (const key of ['results_sheets_by_sample', 'sl_sample_comments', 'sl_sample_photos']) {
    const map = _db[key];
    if (map && typeof map === 'object' && !Array.isArray(map)
        && Object.prototype.hasOwnProperty.call(map, previousId)) {
      map[nextId] = map[previousId];
      delete map[previousId];
    }
  }
  if (Array.isArray(_db.sl_approvals)) {
    _db.sl_approvals = _db.sl_approvals.map(item =>
      String(item.sampleId) === previousId ? { ...item, sampleId: nextId } : item
    );
  }
  if (Array.isArray(_db.sl_invoices)) {
    _db.sl_invoices = _db.sl_invoices.map(invoice => ({
      ...invoice,
      sampleIds: Array.isArray(invoice.sampleIds)
        ? invoice.sampleIds.map(sampleId =>
            String(sampleId) === previousId ? nextId : sampleId
          )
        : invoice.sampleIds,
    }));
  }
}

function announceConflict(kind, identity, current, message) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lims-data-conflict', {
      detail: { kind, identity, current, message },
    }));
  }
}

function announceCompletedSampleLock(identity, current, body) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lims-completed-sample-locked', {
      detail: {
        sampleId: identity,
        current: current || null,
        request: body.request || null,
        message: body.error || 'This completed sample requires administrator approval before editing.',
      },
    }));
  }
}

async function parseMutationResponse(res, kind, identity, collectionKey, matcher) {
  const body = await res.json().catch(() => ({}));
  if (res.status === 423 && body.code === 'COMPLETED_SAMPLE_LOCKED') {
    if (body.current) replaceCached(collectionKey, matcher, body.current);
    announceCompletedSampleLock(identity, body.current || null, body);
    const error = new Error(body.error || 'Completed sample is locked');
    error.code = body.code;
    error.current = body.current;
    throw error;
  }
  if (res.status === 409) {
    if (body.current) replaceCached(collectionKey, matcher, body.current);
    announceConflict(kind, identity, body.current || null, body.error || 'Record changed by another user');
    const error = new Error(body.error || 'Record changed by another user');
    error.code = body.code || 'REVISION_CONFLICT';
    error.current = body.current;
    throw error;
  }
  if (!res.ok) throw new Error(body.error || `Server returned HTTP ${res.status}`);
  if (body.record) replaceCached(collectionKey, matcher, body.record);
  if (body.version) _knownVersion = body.version;
  return body;
}

// Load everything from server into cache
export async function syncAllFromServer() {
  try {
    const res = await apiFetch(`${SERVER_URL}/api/data`);
    if (!res.ok) return false;
    _db = await res.json();
    _ready = true;
    if (_db._reset_version) _resetVersion = _db._reset_version;
    // Apply anything that tried to write while sync was still in progress —
    // these used to be silently dropped instead of applied.
    if (_pendingWrites.length > 0) {
      console.log(`[LIMS] Applying ${_pendingWrites.length} write(s) queued during load...`);
      const queued = _pendingWrites;
      _pendingWrites = [];
      queued.forEach(([key, value]) => syncedSet(key, value));
    }
    // Also grab the current version counter, so the very first poll after
    // load has a real baseline instead of re-fetching everything again
    // immediately just to discover nothing changed yet.
    try {
      const vRes = await apiFetch(`${SERVER_URL}/api/data-version`);
      if (vRes.ok) { const { version } = await vRes.json(); _knownVersion = version; }
    } catch {}
    console.log('[LIMS] Loaded from server:', Object.keys(_db).length, 'keys');
    return true;
  } catch(e) {
    console.error('[LIMS] Load failed:', e.message);
    _ready = true; // allow writes even if server unreachable
    if (_pendingWrites.length > 0) {
      const queued = _pendingWrites;
      _pendingWrites = [];
      queued.forEach(([key, value]) => syncedSet(key, value));
    }
    return false;
  }
}

export function syncedGet(key, fallback) {
  const val = _db[key];
  return (val !== undefined && val !== null) ? val : fallback;
}

export function syncedSet(key, value) {
  if (key === 'sl_session' || key === '_reset_version') return;
  if (key === 'sl_samples' || key === 'sl_invoices') {
    const error = new Error(`${key} must use record-level operations`);
    console.error('[LIMS]', error.message);
    return Promise.reject(error);
  }
  if (!_ready) {
    // Previously this write was just dropped — if nothing else ever
    // retried it, that data was permanently lost. Now it's queued and
    // applied automatically the moment sync finishes instead.
    console.warn('[LIMS] Write queued — sync not complete yet:', key);
    _pendingWrites.push([key, value]);
    return Promise.resolve(false);
  }
  const base = cloneValue(_db[key]);
  const sequence = (_writeSequence[key] || 0) + 1;
  _writeSequence[key] = sequence;
  _db[key] = value;
  _writeTime[key] = Date.now();
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/concurrent-key/${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ baseExists: base !== undefined, base: base === undefined ? null : base, value }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      if (_writeSequence[key] === sequence) {
        if (base === undefined) delete _db[key]; else _db[key] = base;
        _knownVersion = 0;
      }
      throw new Error(error.error || `Server returned HTTP ${res.status}`);
    }
    const result = await res.json();
    if (_writeSequence[key] === sequence && Object.prototype.hasOwnProperty.call(result, 'value')) {
      _db[key] = result.value;
    }
    if (result.version) _knownVersion = result.version;
    return true;
  });
}

export function createSampleRecord(sample) {
  const identity = String(sample.id);
  const optimistic = { ...sample, _revision: Number(sample._revision || 1) };
  replaceCached('sl_samples', item => String(item.id) === identity, optimistic);
  _writeTime.sl_samples = Date.now();
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/records/samples`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sample }),
    });
    return parseMutationResponse(
      res, 'sample', identity, 'sl_samples',
      item => String(item.id) === identity
    );
  });
}

export function updateSampleRecord(id, changes) {
  const identity = String(id);
  const current = (_db.sl_samples || []).find(item => String(item.id) === identity);
  if (!current) return Promise.reject(new Error(`Sample ${identity} not found in cache`));
  replaceCached(
    'sl_samples',
    item => String(item.id) === identity,
    { ...current, ...changes, id: current.id }
  );
  _writeTime.sl_samples = Date.now();
  return enqueueWrite(async () => {
    const latest = (_db.sl_samples || []).find(item => String(item.id) === identity);
    const expectedRevision = Number(latest?._revision || current._revision || 1);
    const res = await apiFetch(`${SERVER_URL}/api/records/samples/${encodeURIComponent(identity)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changes, expectedRevision, base: current }),
    });
    return parseMutationResponse(
      res, 'sample', identity, 'sl_samples',
      item => String(item.id) === identity
    );
  });
}

export function updateSampleIdentityRecord(id, newId, workOrder) {
  const identity = String(id);
  const nextIdentity = String(newId).trim();
  const nextWorkOrder = String(workOrder).trim();
  const current = (_db.sl_samples || []).find(item => String(item.id) === identity);
  if (!current) return Promise.reject(new Error(`Sample ${identity} not found in cache`));
  if (!nextIdentity) return Promise.reject(new Error('Sample ID is required'));
  if (!nextWorkOrder) return Promise.reject(new Error('Work Order is required'));

  return enqueueWrite(async () => {
    const latest = (_db.sl_samples || []).find(item => String(item.id) === identity);
    if (!latest) throw new Error(`Sample ${identity} no longer exists`);
    const expectedRevision = Number(latest._revision || current._revision || 1);
    const res = await apiFetch(
      `${SERVER_URL}/api/records/samples/${encodeURIComponent(identity)}/identity`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newId: nextIdentity,
          workOrder: nextWorkOrder,
          expectedRevision,
        }),
      }
    );
    const body = await res.json().catch(() => ({}));
    if (res.status === 423 && body.code === 'COMPLETED_SAMPLE_LOCKED') {
      if (body.current) replaceCached('sl_samples', item => String(item.id) === identity, body.current);
      announceCompletedSampleLock(identity, body.current || null, body);
      const error = new Error(body.error || 'Completed sample is locked');
      error.code = body.code;
      error.current = body.current;
      throw error;
    }
    if (res.status === 409) {
      if (body.code === 'REVISION_CONFLICT' && body.current) {
        replaceCached(
          'sl_samples',
          item => String(item.id) === identity,
          body.current
        );
      }
      announceConflict(
        'sample',
        identity,
        body.current || null,
        body.error || 'Sample identity changed by another user'
      );
      const error = new Error(body.error || 'Sample identity changed by another user');
      error.code = body.code || 'REVISION_CONFLICT';
      error.current = body.current;
      throw error;
    }
    if (!res.ok) throw new Error(body.error || `Server returned HTTP ${res.status}`);
    if (!body.record) throw new Error('Server did not return the updated sample');
    applySampleIdentityToCache(identity, body.record);
    (body.linkedInvoices || []).forEach(invoice => replaceCached(
      'sl_invoices',
      item => String(item._recordId || item.id || item.num) ===
        String(invoice._recordId || invoice.id || invoice.num),
      invoice
    ));
    _writeTime.sl_samples = Date.now();
    if (body.version) _knownVersion = body.version;
    return body;
  });
}

export function deleteSampleRecord(id) {
  const identity = String(id);
  const current = (_db.sl_samples || []).find(item => String(item.id) === identity);
  if (!current) return Promise.reject(new Error(`Sample ${identity} not found in cache`));
  removeCached('sl_samples', item => String(item.id) === identity);
  _writeTime.sl_samples = Date.now();
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/records/samples/${encodeURIComponent(identity)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expectedRevision: Number(current._revision || 1) }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 409) {
      if (body.current) replaceCached('sl_samples', item => String(item.id) === identity, body.current);
      announceConflict('sample', identity, body.current || null, body.error);
      const error = new Error(body.error || 'Sample changed by another user');
      error.code = 'REVISION_CONFLICT';
      throw error;
    }
    if (!res.ok) {
      replaceCached('sl_samples', item => String(item.id) === identity, current);
      throw new Error(body.error || `Server returned HTTP ${res.status}`);
    }
    if (body.version) _knownVersion = body.version;
    return body;
  });
}

export function createInvoiceRecord(invoice) {
  const identity = String(invoice._recordId || invoice.id || invoice.num);
  const optimistic = { ...invoice, _recordId: identity, _revision: Number(invoice._revision || 1) };
  replaceCached('sl_invoices', item => String(item._recordId) === identity, optimistic);
  _writeTime.sl_invoices = Date.now();
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/records/invoices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice: optimistic }),
    });
    return parseMutationResponse(
      res, 'invoice', identity, 'sl_invoices',
      item => String(item._recordId) === identity
    );
  });
}

export function updateInvoiceRecord(recordId, changes) {
  const identity = String(recordId);
  const current = (_db.sl_invoices || []).find(item => String(item._recordId) === identity);
  if (!current) return Promise.reject(new Error(`Invoice ${identity} not found in cache`));
  replaceCached(
    'sl_invoices',
    item => String(item._recordId) === identity,
    { ...current, ...changes, _recordId: identity }
  );
  _writeTime.sl_invoices = Date.now();
  return enqueueWrite(async () => {
    const latest = (_db.sl_invoices || []).find(item => String(item._recordId) === identity);
    const expectedRevision = Number(latest?._revision || current._revision || 1);
    const res = await apiFetch(`${SERVER_URL}/api/records/invoices/${encodeURIComponent(identity)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changes, expectedRevision, base: current }),
    });
    return parseMutationResponse(
      res, 'invoice', identity, 'sl_invoices',
      item => String(item._recordId) === identity
    );
  });
}

export function deleteInvoiceRecord(recordId) {
  const identity = String(recordId);
  const current = (_db.sl_invoices || []).find(item => String(item._recordId) === identity);
  if (!current) return Promise.reject(new Error(`Invoice ${identity} not found in cache`));
  removeCached('sl_invoices', item => String(item._recordId) === identity);
  _writeTime.sl_invoices = Date.now();
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/records/invoices/${encodeURIComponent(identity)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expectedRevision: Number(current._revision || 1) }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 409) {
      if (body.current) replaceCached('sl_invoices', item => String(item._recordId) === identity, body.current);
      announceConflict('invoice', identity, body.current || null, body.error);
      const error = new Error(body.error || 'Invoice changed by another user');
      error.code = 'REVISION_CONFLICT';
      throw error;
    }
    if (!res.ok) {
      replaceCached('sl_invoices', item => String(item._recordId) === identity, current);
      throw new Error(body.error || `Server returned HTTP ${res.status}`);
    }
    if (body.version) _knownVersion = body.version;
    return body;
  });
}

export function reserveCounterValues(kind, count = 1) {
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/counters/reserve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, count }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Server returned HTTP ${res.status}`);
    if (body.counters) _db.sl_counters = body.counters;
    if (body.version) _knownVersion = body.version;
    return body.values || [];
  });
}

export function syncedRemove(key) {
  const base = cloneValue(_db[key]);
  const sequence = (_writeSequence[key] || 0) + 1;
  _writeSequence[key] = sequence;
  delete _db[key];
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/concurrent-key/${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base: base === undefined ? null : base }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      if (_writeSequence[key] === sequence && base !== undefined) _db[key] = base;
      _knownVersion = 0;
      throw new Error(error.error || `Server returned HTTP ${res.status}`);
    }
    const result = await res.json();
    if (result.version) _knownVersion = result.version;
    return true;
  });
}

// Poll — cheap version check first; only fetches (and deep-compares) the
// full database when the server reports something actually changed. This
// used to fetch and JSON.stringify-compare the ENTIRE database every 5
// seconds regardless of whether anything changed, which got slower and
// slower as more data (especially frequent small writes like chat
// messages) accumulated — this is what was making refresh/navigation feel
// increasingly sluggish over time.
export async function pollForChanges() {
  if (_syncPaused) return false;
  // Never pull an older server snapshot over an optimistic write that is
  // queued or still in flight.
  if (_pendingWriteCount > 0) return false;
  // A hung request here (e.g. mid network-change/reconnect) previously had
  // no timeout at all — it would just sit pending. Since this runs on a
  // fixed 5s interval with no overlap guard on the caller's side, a hung
  // poll plus every subsequent tick piling on top of it is exactly what
  // turns a brief network hiccup into a tab that never recovers.
  const withTimeout = (url, ms = 8000) => {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), ms);
    return apiFetch(url, { signal: controller.signal }).finally(() => clearTimeout(t));
  };
  try {
    const vRes = await withTimeout(`${SERVER_URL}/api/data-version`);
    if (!vRes.ok) return false;
    const { version } = await vRes.json();
    if (version === _knownVersion) return false; // nothing changed — skip the expensive full fetch+compare entirely
    if (_uiEditLocks.size === 0) _knownVersion = version;

    const res = await withTimeout(`${SERVER_URL}/api/data`);
    if (!res.ok) return false;
    const fresh = await res.json();

    // If server was reset, force hard reload on all browsers
    if (fresh._reset_version && fresh._reset_version !== _resetVersion) {
      if (_resetVersion !== 0) {
        console.log('[LIMS] Server was reset — reloading...');
        window.location.reload();
        return false;
      }
      _resetVersion = fresh._reset_version;
    }

    let changed = false;
    const now = Date.now();
    Object.entries(fresh).forEach(([k, v]) => {
      if (k === 'sl_session' || k === '_reset_version') return;
      if (_uiEditLocks.size > 0 && (k === 'sl_samples' || k === 'sl_invoices')) return;
      // Skip keys written locally in last 3 seconds
      if (_writeTime[k] && (now - _writeTime[k]) < 3000) return;
      const cur = JSON.stringify(_db[k]);
      const nxt = JSON.stringify(v);
      if (cur !== nxt) { _db[k] = v; changed = true; }
    });
    // Keep edited form state mounted. Once the form saves or is abandoned,
    // the next poll can safely remount screens with the latest server data.
    return changed && _uiEditLocks.size === 0;
  } catch { return false; }
}

export function clearCacheKeys(keys) {
  keys.forEach(k => delete _db[k]);
  return enqueueWrite(async () => {
    const res = await apiFetch(`${SERVER_URL}/api/data/patch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changes: {}, deletions: keys }),
    });
    if (!res.ok) throw new Error(`Server returned HTTP ${res.status}`);
    const result = await res.json();
    if (result.version) _knownVersion = result.version;
    return true;
  });
}

export async function serverGet(key) {
  return syncedGet(key, null);
}

export async function serverSet(key, value) {
  return syncedSet(key, value);
}

export const SERVER_KEYS = [
  'sl_samples','sl_invoices','sl_counters','sl_pricing',
  'sl_approvals','sl_users',
  'sl_list_testcategories','sl_list_sampleforms',
  'sl_list_sources','sl_list_technicians',
  'sl_param_areas','sl_param_meta',
  'sl_unit_prefs','sl_method_prefs',
  'sl_unit_prefs_L','sl_unit_prefs_S','sl_unit_prefs_T','sl_method_prefs_L','sl_method_prefs_S','sl_method_prefs_T',
  'sl_result_field_defaults_L','sl_result_field_defaults_S','sl_result_field_defaults_T',
  'sl_custom_units','sl_custom_methods',
  'sl_api_settings','sl_no_seed',
  'sl_hours_log','sl_pay_notices','sl_custom_packages',
  'sl_sample_login_queue',
];

export function pauseSync()  { _syncPaused = true; }
export function resumeSync() { _syncPaused = false; }
export function flushWrites() { return _writeQueue; }
export function isServerOnline() {
  return apiFetch(`${SERVER_URL}/health`).then(r => r.ok).catch(() => false);
}
